import {
  useWhatsappTemplates,
  WhatsappTemplateCategory,
  WhatsappTemplateModel,
  WhatsappTemplateStatus,
} from "@jugl-web/rest-api";
import {
  DataLoadingWrapper,
  Dialog,
  Button,
  PlainButton,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { ReactNode, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { WhatsappAccountTab } from "../../../ManageAccountsDrawer/screens/WhatsappAccountScreen/types";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as CommentIcon } from "./assets/comment.svg";
import { TemplateListItem } from "./components/TemplateListItem";

export interface WhatsappTemplatesDialogProps {
  isOpen: boolean;
  isSending?: boolean;
  onRequestClose: () => void;
  onSelectTemplate: (template: WhatsappTemplateModel) => void | Promise<void>;
}

export const WhatsappTemplatesDialog: React.FC<
  WhatsappTemplatesDialogProps
> = ({ isOpen, isSending, onRequestClose, onSelectTemplate }) => {
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const { entityId } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();

  const {
    data: templates,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    isInitialized,
    hasMore,
    loadMore,
    retry,
  } = useWhatsappTemplates({
    entityId,
    type: WhatsappTemplateCategory.message,
    status: WhatsappTemplateStatus.approved,
    enabled: true,
    skip: !isOpen,
  });

  const { ref: inViewRef, inView } = useInView({
    rootMargin: "120px",
  });

  useEffect(() => {
    if (inView && hasMore && !isLoadingMore && !isLoadingMoreError) {
      loadMore();
    }
  }, [hasMore, inView, isLoadingMore, isLoadingMoreError, loadMore]);

  useEffect(() => {
    if (isOpen) {
      setSelectedTemplateId(undefined);
    }
  }, [isOpen]);

  const isInitialLoading =
    (!isInitialized || isLoading) && templates.length === 0;
  const hasNoTemplates =
    !isInitialLoading && !isError && templates.length === 0;

  const openMessageTemplatesSettings = () => {
    onRequestClose();
    openManageAccountDrawer({
      name: "whatsappAccount",
      tab: WhatsappAccountTab.messageTemplates,
    });
  };

  const handleSend = async () => {
    const selectedTemplate = templates.find(
      (template) => template.id === selectedTemplateId
    );

    if (!selectedTemplate) {
      return;
    }

    try {
      await onSelectTemplate(selectedTemplate);
      onRequestClose();
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className="flex h-full max-h-[min(51.25rem,90vh)] w-full max-w-195 flex-col rounded-2xl font-secondary"
    >
      <div className="flex h-15 w-full shrink-0 items-center justify-between border-0 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-3">
          <CommentIcon />
          <span className="font-secondary font-semibold text-[#383838]">
            {t({
              id: "client-support-page.select-and-send",
              defaultMessage: "Select & Send",
            })}
          </span>
        </div>
        <PlainButton
          onClick={onRequestClose}
          className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-grey-100"
        >
          <CloseIcon />
        </PlainButton>
      </div>

      <DataLoadingWrapper
        className="flex min-h-0 flex-1 flex-col"
        isError={isError && templates.length === 0}
        isLoading={isInitialLoading}
        isLoadingMore={isLoadingMore}
        isLoadingMoreError={isLoadingMoreError}
        onRetryLoadMore={loadMore}
        onRetry={retry}
      >
        {hasNoTemplates ? (
          <div className="flex h-full w-full flex-col items-center justify-center px-14 py-10 text-center">
            <span className="font-secondary text-2xl font-medium text-black">
              {t({
                id: "client-support-page.no-templates-yet",
                defaultMessage: "No templates yet",
              })}
            </span>
            <span className="mt-2 w-full font-secondary text-[#828282]">
              {t(
                {
                  id: "client-support-page.create-message-templates-in-inbox-settings",
                  defaultMessage:
                    "You can create new <highlightedText>Message Templates</highlightedText>{br}in WhatsApp Inbox Settings",
                },
                {
                  highlightedText: (text: ReactNode) => (
                    <span className="text-primary-800">{text}</span>
                  ),
                  br: <br />,
                }
              )}
            </span>
            <Button
              className="mt-8"
              size="sm"
              iconEnd={<ArrowRightIcon />}
              onClick={openMessageTemplatesSettings}
            >
              {t({
                id: "client-support-page.go-to-settings",
                defaultMessage: "Go To Settings",
              })}
            </Button>
          </div>
        ) : (
          <div className="jugl__custom-scrollbar flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-14 pt-10 pb-8">
            {templates.map((template) => (
              <TemplateListItem
                key={template.id}
                name={template.name}
                body={template.body}
                isSelected={selectedTemplateId === template.id}
                onSelect={() => setSelectedTemplateId(template.id)}
              />
            ))}
            {hasMore && !isLoading && !isLoadingMore && !isLoadingMoreError && (
              <div ref={inViewRef} />
            )}
          </div>
        )}
      </DataLoadingWrapper>
      {!hasNoTemplates &&
        !isInitialLoading &&
        !(isError && templates.length === 0) && (
          <div className="flex h-18 w-full shrink-0 items-center justify-end gap-4 border-0 border-t border-solid border-dark-100 px-6">
            <Button
              variant="tertiary"
              className="w-30"
              isDisabled={isSending}
              onClick={onRequestClose}
            >
              {t({ id: "common.cancel", defaultMessage: "Cancel" })}
            </Button>
            <Button
              className="w-75"
              isDisabled={!selectedTemplateId || isSending}
              isLoading={isSending}
              iconEnd={<ArrowRightIcon />}
              onClick={handleSend}
            >
              {t({
                id: "client-support-page.send-message",
                defaultMessage: "Send Message",
              })}
            </Button>
          </div>
        )}
    </Dialog>
  );
};
